import { formatDuration, PROMPTS, SUBMISSION_STATUSES, type PromptKey, type SubmissionStatus } from "@kmstories/shared";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Bdi, Ltr } from "../../components/Bdi.js";
import { useToast } from "../../components/Toast.js";
import { useT } from "../../i18n/LocaleProvider.js";
import { api, ApiError } from "../../lib/api.js";

interface Detail {
  submission: {
    id: string;
    kind: string;
    story_title: string | null;
    story_text: string | null;
    note: string | null;
    link_url: string | null;
    prompt_key: PromptKey | null;
    identity_preference: string;
    display_name: string | null;
    email: string | null;
    locale: "en" | "fa";
    status: SubmissionStatus;
    admin_note: string | null;
    spam_flagged: boolean;
    created_at: string;
  };
  consents: { consent_version: number; film_license: boolean; web_excerpts: boolean; future_contact: boolean; updates: boolean; consented_at: string } | null;
  media: Array<{ id: string; media_kind: "video" | "audio"; status: string; reject_reason: string | null; duration_seconds: string | null; mime_type: string | null; original_filename: string | null; strip_verified: boolean | null; has_thumb: boolean }>;
}

export function AdminSubmission() {
  const { m, lp, locale } = useT();
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [d, setD] = useState<Detail | null>(null);
  const [note, setNote] = useState("");
  const [confirm, setConfirm] = useState("");

  useEffect(() => {
    api.get<Detail>(`/api/admin/submissions/${id}`).then((r) => {
      setD(r);
      setNote(r.submission.admin_note ?? "");
    });
  }, [id]);

  if (!d) return <p className="muted">{m.common.loading}…</p>;
  const s = d.submission;

  async function patch(body: Record<string, unknown>) {
    await api.patch(`/api/admin/submissions/${id}`, body);
    const r = await api.get<Detail>(`/api/admin/submissions/${id}`);
    setD(r);
    toast(m.withdraw.saved);
  }

  async function remove(force: boolean) {
    try {
      await api.delete(`/api/admin/submissions/${id}${force ? "?force=1&erasure=1" : ""}`);
      navigate(lp("/admin"));
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) toast(m.admin.deleteRefusedUsed);
      else toast(m.common.error);
    }
  }

  return (
    <div className="form">
      <p>
        <Link to={lp("/admin")}>← {m.admin.queue}</Link>
      </p>
      <div className="grid-2">
        <div className="card">
          <dl className="kv">
            <dt>{m.admin.colKind}</dt>
            <dd>{m.share.formats[s.kind as keyof typeof m.share.formats]?.title ?? s.kind}</dd>
            <dt>{m.admin.colIdentity}</dt>
            <dd>
              {s.identity_preference === "anonymous" ? m.admin.anonymous : <Bdi>{s.display_name}</Bdi>}
              {s.identity_preference === "pseudonym" && <span className="muted"> ({m.admin.pseudonym})</span>}
            </dd>
            <dt>{m.admin.colEmail}</dt>
            <dd>{s.email ? <Ltr>{s.email}</Ltr> : <span className="muted">—</span>}</dd>
            <dt>{m.nav.language}</dt>
            <dd>{s.locale}</dd>
            <dt>{m.admin.colPrompt}</dt>
            <dd>{s.prompt_key ? PROMPTS[s.prompt_key][locale] : <span className="muted">—</span>}</dd>
            <dt>{m.admin.status}</dt>
            <dd>
              <select className="select" value={s.status} onChange={(e) => void patch({ status: e.target.value })}>
                {SUBMISSION_STATUSES.map((st) => (
                  <option key={st} value={st}>
                    {m.admin.statuses[st]}
                  </option>
                ))}
              </select>
            </dd>
          </dl>
          {s.spam_flagged && (
            <p className="notice notice--danger" style={{ marginBlockStart: "1rem" }}>
              {m.admin.spamFlag}{" "}
              <button type="button" className="linkish" onClick={() => void patch({ spamFlagged: false })}>
                {m.admin.unflag}
              </button>
            </p>
          )}
        </div>
        <div className="card">
          <h2 style={{ fontSize: "1.1rem" }}>{m.share.consentHeading}</h2>
          {d.consents ? (
            <div className="flags">
              <span className={`badge ${d.consents.film_license ? "badge--ok" : ""}`}>{m.admin.consentFilm}: {d.consents.film_license ? m.admin.granted : m.admin.notGranted}</span>
              <span className={`badge ${d.consents.web_excerpts ? "badge--ok" : ""}`}>{m.admin.consentWeb}: {d.consents.web_excerpts ? m.admin.granted : m.admin.notGranted}</span>
              <span className={`badge ${d.consents.future_contact ? "badge--ok" : ""}`}>{m.admin.consentContact}: {d.consents.future_contact ? m.admin.granted : m.admin.notGranted}</span>
              <span className="badge">v{d.consents.consent_version} · {new Date(d.consents.consented_at).toLocaleString()}</span>
            </div>
          ) : (
            <p className="muted">—</p>
          )}
          <h2 style={{ fontSize: "1.1rem", marginBlockStart: "1.5rem" }}>{m.admin.note}</h2>
          <textarea className="textarea" style={{ minHeight: "6rem" }} value={note} onChange={(e) => setNote(e.target.value)} />
          <button type="button" className="btn btn--ghost btn--small" style={{ marginBlockStart: "0.5rem" }} onClick={() => void patch({ adminNote: note })}>
            {m.withdraw.save}
          </button>
        </div>
      </div>

      {s.story_title && (
        <h2>
          <Bdi>{s.story_title}</Bdi>
        </h2>
      )}
      {s.story_text && (
        <div className="story-text" dir="auto">
          {s.story_text}
        </div>
      )}
      {s.note && (
        <p className="muted" dir="auto">
          {s.note}
        </p>
      )}
      {s.link_url && (
        <p>
          <a href={s.link_url} target="_blank" rel="noopener noreferrer">
            <Ltr>{s.link_url}</Ltr>
          </a>
        </p>
      )}

      {d.media.map((f) => (
        <div key={f.id} className="card admin-media">
          <div className="flags" style={{ marginBlockEnd: "0.75rem" }}>
            <span className="badge">{f.media_kind}</span>
            <span className={`badge ${f.status === "ready" ? "badge--ok" : "badge--warn"}`}>{f.status}{f.reject_reason ? ` · ${f.reject_reason}` : ""}</span>
            {f.duration_seconds && <span className="badge">{formatDuration(Number(f.duration_seconds), locale)}</span>}
            <span className={`badge ${f.strip_verified ? "badge--ok" : "badge--status"}`}>metadata stripped: {String(f.strip_verified)}</span>
            {f.original_filename && <span className="muted small"><Bdi>{f.original_filename}</Bdi></span>}
          </div>
          {f.status === "ready" && f.media_kind === "video" && <video controls preload="metadata" src={`/api/admin/media/${f.id}`} poster={f.has_thumb ? `/api/admin/media/${f.id}/thumb` : undefined} />}
          {f.status === "ready" && f.media_kind === "audio" && <audio controls preload="metadata" src={`/api/admin/media/${f.id}`} />}
        </div>
      ))}

      <div className="card">
        <h2 style={{ fontSize: "1.1rem", color: "var(--pomegranate)" }}>{m.admin.delete}</h2>
        <p className="muted small">{m.admin.deleteConfirm}</p>
        <div className="field--inline">
          <input className="input" dir="ltr" value={confirm} onChange={(e) => setConfirm(e.target.value)} aria-label="confirm" />
          <button type="button" className="btn btn--danger" disabled={confirm !== "DELETE"} onClick={() => void remove(false)}>
            {m.admin.delete}
          </button>
          {s.status === "used" && (
            <button type="button" className="btn btn--ghost" disabled={confirm !== "DELETE"} onClick={() => void remove(true)}>
              force
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
